import React, { useState, useEffect } from "react";
import { FaAd, FaPaperPlane, FaSpinner, FaRedo, FaVideo, FaImage, FaClock, FaCheckCircle, FaTrash, FaEye, FaChartLine, FaHourglassHalf, FaCheck, FaUpload, FaCopy, FaTimes } from 'react-icons/fa';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import ViewChecker from "../../Common/ViewChecker";
import AdsSubNav from '../../Common/AdsSubNav';
import CountdownTimer from '../../Common/CountdownTimer';

const InfluencerRunningLeads = () => {
  const navigate = useNavigate();
  const [runningLeads, setRunningLeads] = useState([]);
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(50);
  const totalPages = Math.ceil(runningLeads.length / rowsPerPage);
  // Pagination handlers
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };
  const [adViews, setAdViews] = useState({}); // Store views for each ad individually
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Ad ID copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2000);
  };
  const [showModal, setShowModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [checkingViews, setCheckingViews] = useState({}); // Track loading state per ad

  const stats = {
    runningAds: runningLeads.length,
  };

  // Function to fetch leads data
  const fetchLeads = async () => {
    setLoading(true);
    try {
      // Step 1: First API - Running Leads
      const runningResponse = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/get/running/ads`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log("Running Leads Response:", runningResponse.data);
      const runningLeadsData = runningResponse.data.data || [];
      console.log("📊 Running leads data:", runningLeadsData);
      console.log("🔍 First lead proof data:", runningLeadsData[0]?.proofs?.[0]);
      setRunningLeads(runningLeadsData);

      // Remove the automatic current view check since we now do it per ad on button click
      // No need for the second API call here anymore

      setError(null);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load data.");
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showModal) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [showModal]);

  useEffect(() => {
    fetchLeads();
  }, []);

  // Modal functions
  const openModal = (ad) => {
    setSelectedAd(ad);
    setShowModal(true);
    setConfirmSend(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAd(null);
    setConfirmSend(false);
    setIsSubmitting(false);
    setSelectedImage(null);
    setImagePreview(null);
  };

  // API call to submit completion screenshot (second proof)
  const handleSubmitAd = async () => {
    console.log("🚀 handleSubmitAd function called");
    console.log("Current state:", {
      confirmSend,
      selectedAd: selectedAd?.adId,
      selectedImage: selectedImage?.name,
      isSubmitting
    });

    if (!confirmSend) {
      toast.error("Please confirm by checking the checkbox");
      return;
    }

    if (!selectedAd) {
      toast.error("No advertisement selected");
      return;
    }

    if (!selectedImage) {
      toast.error("Please upload a completion screenshot");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("adId", selectedAd.adId);
      formData.append("platform", selectedAd.proofs?.[0]?.platform);
      formData.append("videoUrl", selectedAd.proofs?.[0]?.videoLink);
      formData.append("screenshot", selectedImage);

      // Debug: Log the form data being sent
      console.log("FormData being sent:", {
        adId: selectedAd.adId,
        platform: selectedAd.proofs?.[0]?.platform,
        videoUrl: selectedAd.proofs?.[0]?.videoLink,
        hasScreenshot: !!selectedImage,
        screenshotName: selectedImage?.name
      });

      console.log("Submitting completion screenshot:", {
        adId: selectedAd.adId,
        platform: selectedAd.proofs?.[0]?.platform,
        videoUrl: selectedAd.proofs?.[0]?.videoLink,
        hasScreenshot: !!selectedImage
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/submit/completed/ad`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Completion screenshot submission response:", response.data);
      console.log("📊 Response data structure:", response.data.data?.proofs?.[0]);

      if (response.data.success === true) {
        toast.success("Completion screenshot submitted successfully! Admin will review and approve.");
        closeModal();
        // Redirect to completed section after submission
        setTimeout(() => {
          navigate("/influencer/advertisement/completed");
        }, 1500);
      } else {
        console.error("Submission failed:", response.data);
        toast.error(response.data.message || "Failed to submit completion screenshot");
      }
    } catch (error) {
      console.error("Error submitting completion screenshot:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });

      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.status === 400) {
        toast.error("Invalid data sent to server. Please check your inputs.");
      } else if (error.response?.status === 401) {
        toast.error("Authentication failed. Please login again.");
      } else if (error.response?.status === 500) {
        toast.error("Server error. Please try again later.");
      } else {
        toast.error("Failed to submit completion screenshot. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // New function to check current views for a specific ad
  const checkCurrentViews = async (lead, adIndex) => {
    setCheckingViews((prev) => ({ ...prev, [adIndex]: true }));

    try {
      const platform = lead.proofs?.[0]?.platform;
      const videoUrl = lead.proofs?.[0]?.videoLink;

      if (!platform || !videoUrl) {
        toast.error("Platform or Video URL missing");
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/check/current/view`,
        {
          platform,
          videoUrl,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log("Current Views Response:", response.data);

      // Store views for this specific ad using its adId as key
      setAdViews((prev) => ({
        ...prev,
        [lead.adId]: response.data.views || 0
      }));

      toast.success("Current views updated successfully");
    } catch (error) {
      console.error("Error checking current views:", error);
      toast.error("Failed to check current views");
    } finally {
      setCheckingViews((prev) => ({ ...prev, [adIndex]: false }));
    }
  };

  return (
    <div className="p-2 sm:p-3 md:p-4 lg:p-6 bg-white rounded-lg shadow-md">
      {/* Header and Workflow Guide */}
      <div className="mb-4 sm:mb-6 space-y-4">
        {/* Modern Header - Ultra Compact */}
        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-xl sm:rounded-[1.5rem] shadow-lg p-2.5 sm:p-4 text-white">
          <div className="flex flex-row items-center justify-between gap-2 sm:gap-4">

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate(-1)}
                className="hidden sm:flex px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-[10px] sm:text-xs font-black uppercase tracking-wider border border-white/20 transition-all whitespace-nowrap"
              >
                ← Back
              </button>
              <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg backdrop-blur-sm shadow-inner flex-shrink-0">
                <FaHourglassHalf className="text-[12px] sm:text-base text-white" />
              </div>
              <div className="flex flex-col justify-center">
                <h1 className="text-xs sm:text-lg font-black tracking-tight leading-none mb-0.5 sm:mb-1">Running Ads</h1>
                <p className="text-amber-50 text-[8px] sm:text-[10px] font-bold uppercase tracking-widest leading-none">Active: {runningLeads.length}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="hidden sm:flex flex-col items-end pr-3 border-r border-white/20">
                <div className="text-sm sm:text-base font-black leading-none">{runningLeads.length}</div>
                <div className="text-[8px] sm:text-[9px] text-amber-50 uppercase tracking-widest font-bold">Campaigns</div>
              </div>
              <button
                className="flex items-center justify-center gap-1.5 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors backdrop-blur-sm text-[10px] sm:text-xs tracking-wider font-black uppercase shadow-sm"
                onClick={fetchLeads}
                disabled={loading}
              >
                <FaRedo className={`w - 3 h - 3 ${loading ? "animate-spin" : ""} `} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>

          </div>
        </div>

        {/* Workflow Steps - Modern & Compact */}
        <div className="relative">
          <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gray-100 -translate-y-1/2 hidden md:block z-0"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-4 relative z-10">
            {/* Step 1 */}
            <div className="bg-slate-50/50 p-2 sm:p-4 rounded-xl border border-slate-100 flex items-center md:flex-col md:text-center md:justify-center gap-3 transition-all hover:border-indigo-100">
              <div className={`w - 8 h - 8 sm: w - 10 sm: h - 10 rounded - full flex items - center justify - center font - black text - xs sm: text - sm shrink - 0 transition - colors ${runningLeads.some(lead => ["pending", "approved", "submitted", "completed"].includes(lead.proofs?.[0]?.status))
                ? "bg-green-500 text-white" : "bg-indigo-600 text-white shadow-indigo-100 shadow-lg"
                } `}>
                {runningLeads.some(lead => ["pending", "approved", "submitted", "completed"].includes(lead.proofs?.[0]?.status)) ? <FaCheck /> : "1"}
              </div>
              <div className="min-w-0">
                <h3 className="font-black text-slate-900 text-[10px] sm:text-sm uppercase tracking-tight">Initial Proof</h3>
                <p className="text-[9px] sm:text-xs text-slate-500 font-medium truncate">Submit screenshot & link</p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-slate-50/50 p-2 sm:p-4 rounded-xl border border-slate-100 flex items-center md:flex-col md:text-center md:justify-center gap-3 transition-all hover:border-yellow-100">
              <div className={`w - 8 h - 8 sm: w - 10 sm: h - 10 rounded - full flex items - center justify - center font - black text - xs sm: text - sm shrink - 0 transition - colors ${runningLeads.some(lead => ["approved", "submitted", "completed"].includes(lead.proofs?.[0]?.status))
                ? "bg-green-500 text-white"
                : runningLeads.some(lead => lead.proofs?.[0]?.status === "pending") ? "bg-yellow-500 text-white animate-pulse" : "bg-slate-200 text-slate-400"
                } `}>
                {runningLeads.some(lead => ["approved", "submitted", "completed"].includes(lead.proofs?.[0]?.status)) ? <FaCheck /> : "2"}
              </div>
              <div className="min-w-0">
                <h3 className="font-black text-slate-900 text-[10px] sm:text-sm uppercase tracking-tight">Admin Approval</h3>
                <p className="text-[9px] sm:text-xs text-slate-500 font-medium truncate">Wait for verification</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-slate-50/50 p-2 sm:p-4 rounded-xl border border-slate-100 flex items-center md:flex-col md:text-center md:justify-center gap-3 transition-all hover:border-green-100">
              <div className={`w - 8 h - 8 sm: w - 10 sm: h - 10 rounded - full flex items - center justify - center font - black text - xs sm: text - sm shrink - 0 transition - colors ${runningLeads.some(lead => lead.proofs?.[0]?.status === "completed")
                ? "bg-green-500 text-white"
                : runningLeads.some(lead => lead.proofs?.[0]?.status === "submitted") ? "bg-blue-500 text-white animate-pulse" : "bg-slate-200 text-slate-400"
                } `}>
                {runningLeads.some(lead => lead.proofs?.[0]?.status === "completed") ? <FaCheck /> : "3"}
              </div>
              <div className="flex flex-col min-w-0">
                <h3 className="font-black text-slate-900 text-[10px] sm:text-sm uppercase tracking-tight">Final Completion</h3>
                <p className="text-[9px] sm:text-xs text-slate-500 font-medium truncate">Target Views ≥ Required</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AdsSubNav type="paid" />

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-6 sm:py-8 md:py-12">
          <FaSpinner className="animate-spin text-green-500 text-2xl sm:text-3xl md:text-4xl" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg mb-3 sm:mb-4 md:mb-6 text-xs sm:text-sm">
          <p>{error}</p>
        </div>
      )}

      {/* No Running Leads - Professional Neutral Empty State */}
      {!loading && !error && runningLeads.length === 0 && (
        <div className="max-w-xl mx-auto text-center py-16 px-4 animate-fadeIn">
          <div className="relative inline-block mb-8">
            <div className="absolute inset-0 bg-indigo-400/20 blur-[50px] rounded-full" />
            <div className="relative w-48 h-48 sm:w-56 sm:h-56 rounded-full border-4 border-slate-100 shadow-2xl overflow-hidden mx-auto bg-white flex items-center justify-center ring-8 ring-slate-50/50">
              <FaChartLine className="text-slate-200 text-7xl sm:text-8xl" />
            </div>
          </div>

          <h3 className="text-slate-900 font-black text-2xl tracking-tight mb-2">No Active Progress</h3>
          <p className="text-slate-500 text-sm font-medium leading-relaxed mb-8">
            You don't have any advertisements currently in the running phase. Check back later for updates on your active campaigns.
          </p>
          <button
            onClick={fetchLeads}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-3 rounded-2xl font-black text-sm uppercase tracking-widest hover:shadow-xl hover:shadow-orange-200 transition-all active:scale-95"
          >
            <FaRedo className={loading ? "animate-spin" : ""} />
            Refresh Leads
          </button>
        </div>
      )}

      {/* Running Leads List - Modern Responsive Design */}
      {!loading && !error && runningLeads.length > 0 && (
        <div className="space-y-6">
          {/* Mobile View - Modern Cards */}
          <div className="block md:hidden space-y-4">
            {[...runningLeads]
              .reverse()
              .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
              .map((lead, index) => {
                const absoluteIndex = (currentPage - 1) * rowsPerPage + index;
                const proof = lead.proofs?.[0] || {};
                const isRejected = proof.status === "rejected";
                const isActive = proof.status === "approved";
                const isCompleted = proof.status === "completed";
                const isPending = proof.status === "pending";
                const isFinalSubmitted = proof.status === "submitted";

                return (
                  <div key={absoluteIndex} className={`bg - white border rounded - [1.5rem] shadow - sm overflow - hidden ${isRejected ? 'border-red-200 ring-1 ring-red-100' : 'border-gray-100'} `}>
                    {/* Compact Horizontal Layout */}
                    <div className="flex flex-row">
                      {/* Left: Media Thumbnail */}
                      <div className="w-1/3 min-w-[100px] bg-slate-100 relative overflow-hidden border-r">
                        {proof.screenshot ? (
                          <img src={proof.screenshot} alt="Thumbnail" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-indigo-50">
                            <FaAd className="text-indigo-200 text-2xl" />
                          </div>
                        )}

                        {/* Status Badge on Image */}
                        <div className="absolute top-1 left-1">
                          <span className={`text - [8px] px - 1.5 py - 0.5 rounded - full font - black uppercase tracking - tighter border shadow - sm ${isActive ? "bg-green-500 text-white border-green-400" :
                            isPending ? "bg-yellow-500 text-white border-yellow-400" :
                              isRejected ? "bg-red-500 text-white border-red-400" :
                                "bg-blue-500 text-white border-blue-400"
                            } `}>
                            {isActive ? "Active" : isPending ? "Pending" : isRejected ? "Rejected" : proof.status || "Unknown"}
                          </span>
                        </div>

                        {/* Reward Overlay */}
                        <div className="absolute bottom-1 right-1">
                          <span className="bg-indigo-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full shadow-lg">
                            ₹{lead.finalReporterPrice?.toFixed(0) || "0"}
                          </span>
                        </div>
                      </div>

                      {/* Right: Info Section */}
                      <div className="flex-1 p-3 flex flex-col">
                        {/* Header Row: Name & Price */}
                        <div className="flex justify-between items-start mb-1">
                          <h3 className="text-[11px] font-black text-slate-800 truncate pr-2 uppercase tracking-tight">
                            {proof.channelName || "Campaign"}
                          </h3>
                          <span className="text-xs font-black text-indigo-600 shrink-0">
                            ₹{lead.finalReporterPrice?.toFixed(0)}
                          </span>
                        </div>

                        {/* Metadata Row: ID & Platform */}
                        <div className="flex items-center gap-1.5 text-[9px] font-bold text-slate-400 mb-2">
                          <span className="bg-slate-50 px-1 py-0.5 rounded border border-slate-100">REF: {(lead.adId || "").substring(0, 6).toUpperCase()}</span>
                          <span className="text-slate-200">|</span>
                          <span className="uppercase text-purple-600">{proof.platform}</span>
                        </div>

                        {/* Mid Section: Time & Target */}
                        <div className="space-y-1.5 mb-3">
                          <div className="flex items-center gap-2 text-[10px] font-bold text-slate-600">
                            <span className="text-amber-500 w-4 flex justify-center">⏳</span>
                            <div className="flex items-center gap-1">
                              <CountdownTimer
                                targetDate={lead.expiresAt}
                                prefix=""
                                expiredText="EXPIRED"
                              />
                              <span className="text-slate-400 text-[9px]">left</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 text-[10px] font-bold text-slate-600">
                            <span className="text-indigo-500 w-4 flex justify-center">🎯</span>
                            <span>{lead.baseView || 0} Required</span>
                          </div>
                        </div>

                        {/* Progress Section */}
                        <div className="bg-slate-50/80 rounded-xl p-2 border border-slate-100 mb-3">
                          <div className="flex items-center justify-between">
                            <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Live Progress</span>
                            <ViewChecker
                              platform={proof.platform}
                              videoLink={proof.videoLink}
                              adId={lead.adId}
                              proofIndex={0}
                              onViewsFound={(views) => {
                                setAdViews(prev => ({ ...prev, [lead.adId]: views }));
                              }}
                            />
                          </div>
                        </div>

                        {/* Footer Status & Actions */}
                        <div className="mt-auto">
                          {isPending ? (
                            <div className="flex flex-col gap-1 px-3 py-2 bg-rose-50 border border-rose-100 rounded-xl">
                              <div className="flex items-center gap-2 text-rose-600">
                                <span className="text-xs animate-pulse">⚠</span>
                                <span className="text-[10px] font-black uppercase tracking-tight">Initial Proof Pending</span>
                              </div>
                              {(adViews[lead.adId] || 0) >= lead.baseView && (
                                <div className="text-[8px] font-bold text-emerald-600 uppercase tracking-widest pl-5">
                                  Goal Reached! Wait for Approval
                                </div>
                              )}
                            </div>
                          ) : isActive ? (
                            (proof.platform?.toLowerCase() === "instagram" || (adViews[lead.adId] || 0) >= lead.baseView) ? (
                              <button
                                onClick={() => openModal(lead)}
                                className="w-full py-2 bg-green-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-green-100 flex items-center justify-center gap-2 border-b-2 border-green-700 active:scale-95 transition-all"
                              >
                                <FaUpload className="text-[9px]" /> Submit Final Proof
                              </button>
                            ) : (
                              <div className="grid grid-cols-2 gap-2">
                                <a
                                  href={proof.videoLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="py-2 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
                                >
                                  <FaPaperPlane className="text-[9px]" /> Open
                                </a>
                                <div className="py-2 bg-slate-100 text-slate-400 rounded-xl text-[9px] font-black uppercase text-center flex flex-col justify-center leading-none">
                                  <span>{Math.max(0, lead.baseView - (adViews[lead.adId] || 0))} More</span>
                                  <span className="text-[7px] text-indigo-500/60 mt-0.5">Views Left</span>
                                </div>
                              </div>
                            )
                          ) : isFinalSubmitted ? (
                            <div className="py-2 bg-amber-50 border border-amber-100 text-amber-600 rounded-xl text-[10px] font-black uppercase text-center animate-pulse">
                              Admin Reviewing Final
                            </div>
                          ) : isRejected ? (
                            proof.initialProofRejectNote ? (
                              <button
                                onClick={() => navigate("/influencer/advertisement/accepted")}
                                className="w-full py-2 bg-orange-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg flex items-center justify-center gap-2 border-b-2 border-orange-700 active:scale-95 transition-all"
                              >
                                <FaRedo className="text-[9px]" /> Fix Initial Proof
                              </button>
                            ) : (
                              <button
                                onClick={() => openModal(lead)}
                                className="w-full py-2 bg-rose-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-rose-100 flex items-center justify-center gap-2 border-b-2 border-rose-700 active:scale-95 transition-all"
                              >
                                <FaUpload className="text-[9px]" /> Resubmit Final
                              </button>
                            )
                          ) : (
                            <div className="py-2 bg-indigo-50 border border-indigo-100 text-indigo-600 rounded-xl text-[10px] font-black uppercase text-center">
                              {proof.status || "In Progress"}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Rejection Note if exists */}
                    {
                      isRejected && (
                        <div className="bg-red-50 px-3 py-2 border-t border-red-100 animate-fadeIn">
                          <p className="text-[9px] font-bold text-red-600 flex items-center gap-2">
                            <span className="w-4 h-4 bg-red-100 rounded-full flex items-center justify-center text-[8px]">!</span>
                            {proof.initialProofRejectNote || proof.adminRejectNote || "Fix required before payment."}
                          </p>
                        </div>
                      )
                    }
                  </div>
                );
              })}
          </div>

          {/* Desktop View - Modern Table */}
          <div className="hidden md:block overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-slate-500">
                    <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest">#</th>
                    <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest">Ad Details</th>
                    <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest">Iinsaf ID</th>
                    <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest">Budget</th>
                    <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest">Required Views</th>
                    <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest">Progress</th>
                    <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest">Deadline</th>
                    <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest">Status</th>
                    <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[...runningLeads]
                    .reverse()
                    .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                    .map((lead, index) => {
                      const absoluteIndex = (currentPage - 1) * rowsPerPage + index;
                      const proof = lead.proofs?.[0] || {};
                      const isPending = proof.status === "pending";
                      const isActive = proof.status === "approved";
                      const isReady = isActive && (adViews[lead.adId] || 0) >= lead.baseView;

                      return (
                        <tr key={absoluteIndex} className="hover:bg-blue-50/30 transition-colors group">
                          <td className="px-5 py-4 whitespace-nowrap text-gray-400 font-medium text-xs">
                            {absoluteIndex + 1}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1.5">
                              <div className="flex items-center gap-2">
                                <span className="text-slate-900 font-bold text-xs truncate max-w-[150px]">{proof.channelName || "No Channel"}</span>
                                <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-[9px] font-black uppercase">{proof.platform}</span>
                              </div>
                              <div className="flex items-center gap-2 text-slate-400">
                                <span className="font-mono text-[10px]">{(lead.adId || "").substring(0, 8).toUpperCase()}</span>
                                <button onClick={() => handleCopy(lead.adId, lead.adId)} className="hover:text-indigo-600 transition-colors">
                                  {copiedId === lead.adId ? <FaCheck className="text-green-500" /> : <FaCopy />}
                                </button>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap text-slate-600 font-medium">
                            {proof.iinsafId || "-"}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap font-black text-slate-900">
                            ₹{lead.finalReporterPrice?.toFixed(0) || "0"}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap text-indigo-600 font-black">
                            {lead.baseView || "0"}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              <ViewChecker
                                platform={proof.platform}
                                videoLink={proof.videoLink}
                                adId={lead.adId}
                                proofIndex={0}
                                onViewsFound={(views) => {
                                  setAdViews(prev => ({ ...prev, [lead.adId]: views }));
                                }}
                              />
                              {(proof.platform?.toLowerCase() === "instagram" || (adViews[lead.adId] || 0) >= lead.baseView) && (
                                <span className="text-[8px] text-emerald-600 font-black uppercase tracking-tighter animate-pulse">Reached!</span>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="text-[10px] text-rose-500 font-black">
                                <CountdownTimer targetDate={lead.expiresAt} prefix="" expiredText="EXPIRED" />
                              </span>
                              <span className="text-[8px] text-slate-400 font-bold">{new Date(proof.submittedAt).toLocaleDateString()}</span>
                            </div>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            {isPending ? (
                              <div className="flex flex-col gap-1">
                                <span className="px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest bg-rose-50 text-rose-600 border border-rose-100 animate-pulse">
                                  Initial Pending
                                </span>
                                {(adViews[lead.adId] || 0) >= lead.baseView && (
                                  <span className="text-[7px] text-center text-emerald-600 font-bold uppercase">Target Met</span>
                                )}
                              </div>
                            ) : (
                              <div className="flex flex-col gap-1 items-start">
                                <span className={`px - 2.5 py - 1 rounded - full text - [9px] font - black uppercase tracking - widest border ${proof.status === "approved" ? "bg-green-50 text-green-600 border-green-100" :
                                  proof.status === "submitted" ? "bg-blue-50 text-blue-600 border-blue-100" :
                                    proof.status === "rejected" ? "bg-red-50 text-red-600 border-red-100" :
                                      "bg-slate-50 text-slate-600 border-slate-100"
                                  } `}>
                                  {proof.status === "approved" ? "Active (Live)" : proof.status}
                                </span>
                                {proof.status === "rejected" && (
                                  <span className="text-[9px] text-red-500 font-bold truncate max-w-[120px]" title={proof.initialProofRejectNote || proof.adminRejectNote || "Fix required before payment."}>
                                    Note: {proof.initialProofRejectNote || proof.adminRejectNote || "Fix required before payment."}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            {proof.status === "rejected" ? (
                              proof.initialProofRejectNote ? (
                                <button onClick={() => navigate("/influencer/advertisement/accepted")} className="text-orange-600 hover:text-orange-800 font-black text-xs uppercase tracking-widest border-b-2 border-orange-200">Fix Initial</button>
                              ) : (
                                <button onClick={() => openModal(lead)} className="text-rose-600 hover:text-rose-800 font-black text-xs uppercase tracking-widest border-b-2 border-rose-200">Fix Final</button>
                              )
                            ) : isActive && (proof.platform?.toLowerCase() === "instagram" || (adViews[lead.adId] || 0) >= lead.baseView) ? (
                              <button onClick={() => openModal(lead)} className="bg-indigo-600 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 hover:shadow-lg transition-all border-b-2 border-indigo-800 shadow-sm">Submit Final</button>
                            ) : (
                              <div className="flex flex-col">
                                <span className="text-slate-400 text-[10px] font-bold uppercase tracking-tighter">
                                  {isActive ? "Waiting for views" : isPending ? "Wait for Admin" : "In Review"}
                                </span>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls - Shared */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-3 sm:mt-4 md:mt-6 px-2 sm:px-4">
              <nav className="inline-flex items-center space-x-1 sm:space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px - 3 py - 1 rounded border border - yellow - 400 text - yellow - 800 bg - white hover: bg - yellow - 100 disabled: opacity - 50 disabled: cursor - not - allowed`}
                >
                  Prev
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => handlePageChange(i + 1)}
                    className={`px - 3 py - 1 rounded border border - yellow - 400 hover: bg - yellow - 100 ${currentPage === i + 1 ? 'bg-yellow-300 text-yellow-900 font-bold' : 'bg-white text-yellow-800'} `}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px - 3 py - 1 rounded border border - yellow - 400 text - yellow - 800 bg - white hover: bg - yellow - 100 disabled: opacity - 50 disabled: cursor - not - allowed`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </div>
      )
      }

      {/* Modal for submitting advertisement data - Modern Design */}
      {
        showModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl transform transition-all border border-gray-100 overflow-hidden relative">
              {/* Decorative Top Border */}
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-500 to-green-500" />

              {/* Modal Header */}
              <div className="flex justify-between items-start px-6 pt-6 pb-4 border-b border-gray-100">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
                    <FaPaperPlane className="text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {selectedAd?.proofs?.[0]?.status === "rejected" ? "Resubmit Final Proof" : "Submit Final Proof"}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedAd?.proofs?.[0]?.status === "rejected"
                        ? "Please upload a valid completion screenshot."
                        : "Upload proof showing required views."}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <FaTimes className="text-lg" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[70vh]">
                {selectedAd && (
                  <div className="space-y-6">
                    {/* Key Details Card */}
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Ad Campaign Details</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6">
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Ad Reference</span>
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono font-medium text-gray-900 text-sm">{selectedAd.adId?.substring(0, 8)}...</span>
                            <button onClick={() => handleCopy(selectedAd.adId, "modal-id")} className="text-gray-400 hover:text-blue-500">
                              {copiedId === "modal-id" ? <FaCheck className="text-xs text-green-500" /> : <FaCopy className="text-xs" />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Platform</span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-purple-100 text-purple-700">
                            {selectedAd.proofs?.[0]?.platform || "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Target Views</span>
                          <span className="font-black text-indigo-600 text-lg">
                            {selectedAd.proofs?.[0]?.platform?.toLowerCase() === "instagram" ? "N/A" : (selectedAd.baseView || "0")}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Current Views</span>
                          <div className="flex items-center gap-2">
                            <span className={`font - black text - lg ${selectedAd.proofs?.[0]?.platform?.toLowerCase() === "instagram" ? "text-emerald-600" : (adViews[selectedAd.adId] || 0) >= selectedAd.baseView ? "text-emerald-600" : "text-amber-600"} `}>
                              {selectedAd.proofs?.[0]?.platform?.toLowerCase() === "instagram" ? "N/A" : (adViews[selectedAd.adId] || 0)}
                            </span>
                            {(selectedAd.proofs?.[0]?.platform?.toLowerCase() === "instagram" || (adViews[selectedAd.adId] || 0) >= selectedAd.baseView) && (
                              <FaCheck className="text-emerald-500 text-sm" />
                            )}
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Your Reward</span>
                          <span className="font-black text-slate-900 text-lg">₹{selectedAd.finalReporterPrice?.toFixed(0)}</span>
                        </div>
                      </div>

                      {/* Video Link Bar */}
                      <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500">Target Video</span>
                        {selectedAd.proofs?.[0]?.videoLink ? (
                          <a href={selectedAd.proofs[0].videoLink} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1.5">
                            Open Video <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                          </a>
                        ) : <span className="text-sm text-gray-400">No link available</span>}
                      </div>
                    </div>

                    {/* Check Current Views Helper */}
                    {selectedAd.proofs?.[0]?.platform?.toLowerCase() === "instagram" ? (
                      <div className="bg-green-50 border border-green-100 rounded-xl p-4 flex items-start gap-3">
                        <div className="bg-green-100 p-2 rounded-lg text-green-600 shrink-0">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <div>
                          <h4 className="font-bold text-green-900 text-sm">Instagram Submission Ready</h4>
                          <p className="text-xs text-green-700 mt-0.5">
                            Views verification is not required for Instagram campaigns. Please upload your completion screenshot directly.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3">
                        <div className="bg-blue-100 p-2 rounded-lg text-blue-600 shrink-0">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <div>
                          <h4 className="font-bold text-blue-900 text-sm">Verify Views First</h4>
                          <p className="text-xs text-blue-700 mt-0.5">
                            Please ensure your video has reached at least <strong>{selectedAd.baseView} views</strong> before submitting. Submitting early leads to rejection.
                          </p>
                          <div className="mt-2 text-xs font-bold text-blue-800">
                            Current Detected: {adViews[selectedAd?.adId] || "Not checked"}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Upload Section */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Upload Completion Screenshot <span className="text-red-500">*</span>
                      </label>

                      {!imagePreview ? (
                        <div className="relative border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-500 hover:bg-blue-50/30 transition-all group cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                if (!file.type.startsWith("image/")) {
                                  toast.error("Only image files are allowed.");
                                  setSelectedImage(null);
                                  setImagePreview(null);
                                  e.target.value = null;
                                  return;
                                }
                                setSelectedImage(file);
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setImagePreview(reader.result);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            disabled={isSubmitting}
                            required
                          />
                          <div className="flex flex-col items-center">
                            <div className="w-12 h-12 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center mb-3 group-hover:bg-blue-100 group-hover:text-blue-500 transition-colors">
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                            </div>
                            <p className="text-sm font-medium text-gray-700">Click to upload screenshot</p>
                            <p className="text-xs text-gray-400 mt-1">SVG, PNG, JPG or GIF (max. 10MB)</p>
                          </div>
                        </div>
                      ) : (
                        <div className="relative rounded-xl overflow-hidden border border-gray-200">
                          <img src={imagePreview} alt="Preview" className="w-full h-48 sm:h-64 object-contain bg-gray-50" />
                          <div className="absolute top-2 right-2 flex gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedImage(null);
                                setImagePreview(null);
                              }}
                              className="bg-white/90 backdrop-blur text-red-500 p-2 rounded-lg shadow-sm hover:bg-red-50 transition-colors border border-gray-200"
                              title="Remove image"
                            >
                              <FaTimes />
                            </button>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 bg-white/90 backdrop-blur p-2 border-t border-gray-200 flex items-center gap-2">
                            <FaCheck className="text-green-500" />
                            <span className="text-xs font-medium text-gray-700">Screenshot selected successfully</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Confirmation */}
                    <div className="flex gap-3 items-start pt-2">
                      <div className="flex items-center h-5">
                        <input
                          id="confirm-send"
                          type="checkbox"
                          checked={confirmSend}
                          onChange={(e) => setConfirmSend(e.target.checked)}
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 focus:ring-2 cursor-pointer"
                        />
                      </div>
                      <label htmlFor="confirm-send" className="text-sm text-gray-600 cursor-pointer select-none">
                        {selectedAd.proofs?.[0]?.platform?.toLowerCase() === "instagram"
                          ? "I confirm that this screenshot is authentic. Accessing false claims may lead to account suspension."
                          : "I confirm that I have met the view requirements and this screenshot is authentic. Accessing false claims may lead to account suspension."}
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex flex-col-reverse sm:flex-row justify-end gap-3 rounded-b-2xl">
                <button
                  onClick={closeModal}
                  className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl text-sm hover:bg-gray-50 hover:border-gray-300 transition-all"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    console.log("🔘 Submit button clicked");
                    if (confirmSend) {
                      handleSubmitAd();
                    } else {
                      toast.warning("Please confirm the requirements first.");
                    }
                  }}
                  disabled={!confirmSend || isSubmitting || !selectedImage}
                  className={`px - 8 py - 2.5 text - white font - bold rounded - xl text - sm shadow - lg shadow - blue - 200 transition - all flex items - center gap - 2 transform active: scale - 95 ${!confirmSend || isSubmitting || !selectedImage
                    ? "bg-gray-300 cursor-not-allowed shadow-none"
                    : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                    } `}
                >
                  {isSubmitting ? (
                    <>
                      <FaSpinner className="animate-spin text-lg" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <FaPaperPlane />
                      <span>{selectedAd?.proofs?.[0]?.status === "rejected" ? "Resubmit Proof" : "Submit Proof"}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

    </div >
  );
};





export default InfluencerRunningLeads;

